var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import hive from '@bombas/hive';
import { deepGet, sortMapKeys } from '../../util/MapUtils';
import { replaceStateNextCompat } from '../../util/history';
/**
 * Recursive helper function that compiles an array of all possible size combinations in order of default preference
 * @returns - does not return a value - adds values to external array productSizeCombos
 */
var createSizeCombos = function (
/**
 * array used to build current size combination
 */
sizeCombination, 
/**
 * array of size values corresponding to one size options set
 */
currentSizes, 
/**
 * array of arrays corresponding to remaining size option sets for the product
 */
additionalSizes, 
/**
 * array storing all size combinations in preference order
 */
results) {
    if (sizeCombination === void 0) { sizeCombination = []; }
    if (currentSizes === void 0) { currentSizes = []; }
    if (additionalSizes === void 0) { additionalSizes = []; }
    if (results === void 0) { results = []; }
    if (!currentSizes.length) {
        return;
    }
    for (var i = 0; i < currentSizes.length; i += 1) {
        sizeCombination.push(currentSizes[i]);
        createSizeCombos(sizeCombination, additionalSizes[0], additionalSizes.slice(1), results);
        if (!additionalSizes.length) {
            // @ts-ignore
            results.push(Array.from(sizeCombination));
        }
        sizeCombination.pop();
    }
    if (!additionalSizes.length) {
        return results;
    }
};
/**
 * Given a list of size groups and a demographic,
 * returns a map of each size group and its default size.
 */
var getDefaultSizes = function (
/**
 * Array of size groups for the product
 */
sizes, 
/**
 * Demographic information containing gender and age
 */
demographic) {
    return demographic
        ? sizes.reduce(function (acc, size) {
            var _a, _b;
            var defaultSize = ['MALE'].includes(demographic.gender) &&
                size.options.find(function (s) { return s.id === 'l'; })
                ? 'l'
                : (['FEMALE', 'UNISEX'].includes(demographic.gender) &&
                    ((_a = size.options.find(function (s) { return ['m', 'm/l'].includes(s.id); })) === null || _a === void 0 ? void 0 : _a.id)) ||
                    ((_b = size.options[0]) === null || _b === void 0 ? void 0 : _b.id);
            acc.set(size.id, defaultSize);
            return acc;
        }, new Map())
        : new Map();
};
/**
 * Given a list of size groups and the default value for each group,
 * generates list of size options in preferred order.
 */
var generateSizeSets = function (
/**
 * Array of size groups for the product
 */
sizes, 
/**
 * Map containing default size values for each size group
 */
defaults) {
    return sizes.map(function (sizeGroup) {
        var sizeGroupId = sizeGroup.id;
        var groupsSizes = sizeGroup.options.map(function (size) { return size.id; });
        var defaultSize = defaults.get(sizeGroupId);
        if (defaultSize) {
            var reorderedSizes_1 = [defaultSize];
            groupsSizes.forEach(function (size) {
                if (size !== defaultSize)
                    reorderedSizes_1.push(size);
            });
            groupsSizes = reorderedSizes_1;
        }
        return groupsSizes;
    });
};
/**
 * Helper to preselect a color and size option based on gender and variant availability.
 * @returns Ordered map containing default color and size(s) for the product
 */
export var getDefaults = function (_a) {
    // Implementation note:
    // The goal of this implementation is to make the flow linear. Aside from a small validation step,
    // any value set in `defaults` is treated as immutable to ensure a predictable flow with no side effects.
    var _b;
    var 
    /**
     * Array of colors for the product
     */
    _c = _a.colors, 
    /**
     * Array of colors for the product
     */
    colors = _c === void 0 ? [] : _c, 
    /**
     * Array of size sets for the product
     */
    _d = _a.sizes, 
    /**
     * Array of size sets for the product
     */
    sizes = _d === void 0 ? [] : _d, 
    /**
     * String representing a preselected color from URL params, if any
     */
    _e = _a.selectedColor, 
    /**
     * String representing a preselected color from URL params, if any
     */
    selectedColor = _e === void 0 ? undefined : _e, 
    /**
     * Array representing preselected sizes from URL params, if any
     */
    _f = _a.selectedSizes, 
    /**
     * Array representing preselected sizes from URL params, if any
     */
    selectedSizes = _f === void 0 ? [] : _f, 
    /**
     * Array of demographic objects with gender and age for the product
     */
    _g = _a.demographics, 
    /**
     * Array of demographic objects with gender and age for the product
     */
    demographics = _g === void 0 ? null : _g, 
    /**
     * Map of all variants for the product
     */
    _h = _a.variantsMap, 
    /**
     * Map of all variants for the product
     */
    variantsMap = _h === void 0 ? new Map() : _h;
    var KEY_ORDER = __spreadArray(['color'], sizes.map(function (s) { return s.id; }), true);
    var defaults = new Map();
    // 1. Determine preset defaults based on params
    if (colors.length && selectedColor) {
        defaults.set('color', selectedColor);
    }
    if (sizes.length && selectedSizes.length) {
        selectedSizes.forEach(function (size) {
            defaults.set(size.groupName, size.size);
        });
    }
    // 2. Validate any preset values; if they don't match- remove them.
    defaults.forEach(function (value, key, self) {
        if (key === 'color' && !colors.find(function (c) { return c.id === value; })) {
            hive.track('PDP_PRESELECTED_COLOR_REJECTED');
            self.delete(key);
        }
        else if (key.includes('size')) {
            var sizeGroup = sizes.find(function (s) { return s.id === key; });
            if (!sizeGroup || !sizeGroup.options.find(function (sg) { return sg.id === value; })) {
                self.delete(key);
            }
        }
    });
    // 3. Short-circuit if we've covered all defaults.
    if (defaults.size === (colors.length > 0 ? 1 : 0) + sizes.length) {
        if (!colors.length) {
            defaults.set('color', undefined);
        }
        return sortMapKeys(defaults, KEY_ORDER);
    }
    // 4. Inject any unspecified defaults based on demographics and availability
    var specifiedSizeIds = sizes
        .filter(function (s) { return defaults.has(s.id); })
        .map(function (s) { return s.id; });
    // If there are colors and we only need to specify a color
    if (colors.length &&
        !defaults.has('color') &&
        specifiedSizeIds.length === sizes.length) {
        // Find first available color option and preselect it
        var firstAvailableColor = colors.find(function (c) {
            var variant = deepGet(variantsMap, __spreadArray([c.id], sizes.map(function (s) { return defaults.get(s.id); }), true), {});
            return variant.available;
        });
        if (firstAvailableColor) {
            defaults.set('color', firstAvailableColor.id);
        }
        else {
            defaults.set('color', colors[0].id);
        }
    }
    // Need to specify color and/or size(s)
    else {
        if (!colors.length) {
            // NOTE: Setting `color = undefined` is a hack and we shouldn't need to do it.
            // To limit scope of this refactor, the existing logic is left in-place.
            defaults.set('color', undefined);
        }
        // Find preferred default size for each size group, based on demographic data
        var demographic = (demographics === null || demographics === void 0 ? void 0 : demographics.length) === 1 ? demographics[0] : undefined;
        var preferredDefaultSizes = getDefaultSizes(sizes, demographic);
        var sizeSets = generateSizeSets(sizes, preferredDefaultSizes);
        var productSizeCombinations = [];
        createSizeCombos([], 
        // @ts-ignore
        sizeSets[0], sizeSets.slice(1), productSizeCombinations);
        // Find first available color option and preselect it
        // Iterate through size combinations to find first one that is available
        var includedIndices_1 = specifiedSizeIds.map(function (id) { return ({
            position: sizes.findIndex(function (s) { return s.id === id; }),
            value: defaults.get(id)
        }); });
        var possibleSizes = productSizeCombinations.filter(function (combo) {
            return includedIndices_1.every(function (idx) { return combo[idx.position] === idx.value; });
        });
        var possibleColors = defaults.has('color')
            ? [{ id: defaults.get('color') }]
            : colors;
        // Iterate through the variant map to find the first
        // in stock color and size combination and return that as the default
        var foundAvailable = false;
        for (var x = 0; x < possibleColors.length; x += 1) {
            var _loop_1 = function (y) {
                var variant = deepGet(variantsMap, __spreadArray([(_b = possibleColors[x]) === null || _b === void 0 ? void 0 : _b.id], possibleSizes[y], true), false);
                var isAvailable = variant === null || variant === void 0 ? void 0 : variant.available;
                if (isAvailable) {
                    if (!defaults.has('color')) {
                        defaults.set('color', possibleColors[x].id);
                    }
                    sizes.forEach(function (group) {
                        if (!specifiedSizeIds.includes(group.id)) {
                            defaults.set(group.id, variant.sizes.find(function (s) { return s.title === group.id; }).id);
                        }
                    });
                    foundAvailable = true;
                    return "break";
                }
            };
            for (var y = 0; y < possibleSizes.length; y += 1) {
                var state_1 = _loop_1(y);
                if (state_1 === "break")
                    break;
            }
            // This is only necessary due to the `color = undefined` condition above.
            if (defaults.size > 1)
                break;
        }
        // No available variants were found
        if (!foundAvailable) {
            if (colors.length && !defaults.has('color')) {
                defaults.set('color', colors[0].id);
            }
            if (preferredDefaultSizes.size) {
                preferredDefaultSizes.forEach(function (val, key) { return defaults.set(key, val); });
            }
        }
    }
    return sortMapKeys(defaults, KEY_ORDER);
};
/**
 * updates the product url params based on the selected options without reloading the page
 */
export var updateURL = function (
/**
 * a map of color and size options
 */
options) {
    // Early return if not in browser environment to prevent hydration issues
    if (typeof window === 'undefined') {
        return;
    }
    var color = options.get('color');
    var sizes = [];
    options.forEach(function (value, key) {
        if (key.includes('size')) {
            sizes.push([key, value]);
        }
    });
    var nextUrl = new URL(window.location.href);
    if (color)
        nextUrl.searchParams.set('variant', color);
    sizes.forEach(function (_a) {
        var sizeType = _a[0], value = _a[1];
        if (sizeType && value) {
            nextUrl.searchParams.set(sizeType, value);
        }
    });
    replaceStateNextCompat(null, '', nextUrl);
};
/**
 * Evaluates whether a given product attribute rule matches the provided product attribute values.
 */
var checkRuleMatch = function (
/**
 * The ProductAttributeRule from Contentful settings to evaluate
 */
rule, 
/**
 * The product attribute values to check against the rule.
 */
productAttributeValues) {
    if (productAttributeValues == null || !rule.key) {
        return {};
    }
    var isExcluded = rule.excluded || false;
    var ruleValues = rule.values || [];
    // Normalize product values to array for consistent processing
    var productValues = Array.isArray(productAttributeValues)
        ? productAttributeValues.map(function (v) { return v.toLowerCase(); })
        : [String(productAttributeValues).toLowerCase()];
    var normalizedRuleValues = ruleValues.map(function (v) { return v.toLowerCase(); });
    // Check for matches between product values and rule values
    var hasAnyMatch = normalizedRuleValues.some(function (ruleValue) {
        return productValues.includes(ruleValue);
    });
    var hasAllMatches = normalizedRuleValues.every(function (ruleValue) {
        return productValues.includes(ruleValue);
    });
    if (isExcluded) {
        // For exclusion rules: if ANY rule value matches, disqualify
        return hasAnyMatch ? { disqualified: true } : { shouldInclude: true };
    }
    // For inclusion rules: ALL rule values must match
    return hasAllMatches ? { shouldInclude: true } : {};
};
/**
 * Helper function to get the value for a rule key from either productAttributes or productData
 * This allows rules to reference both attributes (product.attributes.key) and top-level product fields
 */
var getValueForRuleKey = function (
/**
 * The rule key to look up
 */
key, 
/**
 * Full product data object
 */
productData) {
    if (!productData) {
        return undefined;
    }
    // Check productAttributes first
    var attributes = productData.attributes;
    if ((attributes === null || attributes === void 0 ? void 0 : attributes[key]) != null) {
        return attributes[key];
    }
    // Handle special demographic field mappings
    if (key === 'gender' && Array.isArray(productData.demographics)) {
        return productData.demographics
            .map(function (demo) { var _a; return (_a = demo.gender) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
            .filter(Boolean);
    }
    if (key === 'age' && Array.isArray(productData.demographics)) {
        return productData.demographics
            .map(function (demo) { var _a; return (_a = demo.age) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
            .filter(Boolean);
    }
    // Get value from product data
    var value = productData[key];
    if (value == null) {
        return undefined;
    }
    // Normalize value based on type
    if (Array.isArray(value) || typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        return ['collections', 'categories'].includes(key) ? [value] : value;
    }
    return value;
};
/**
 * Determines if an attribute should be included based on its rules
 */
var shouldIncludeAttribute = function (
/**
 * The attribute to evaluate
 */
attribute, 
/**
 * The full product data object
 */
productData) {
    var rules = attribute.referenceAttributes || [];
    // Validate attribute has required properties
    if (!attribute.label || !rules.length) {
        return false;
    }
    // Check for any exclusion rule violations first
    var hasExclusionViolation = rules.some(function (rule) {
        if (!rule.key || !rule.excluded)
            return false;
        var value = getValueForRuleKey(rule.key === 'collection' ? 'collections' : rule.key, productData);
        return checkRuleMatch(rule, value).disqualified;
    });
    if (hasExclusionViolation) {
        return false;
    }
    // Check that all inclusion rules match
    var inclusionRules = rules.filter(function (rule) { return rule.key && !rule.excluded; });
    return inclusionRules.every(function (rule) {
        var value = getValueForRuleKey(rule.key === 'collection' ? 'collections' : rule.key, productData);
        return checkRuleMatch(rule, value).shouldInclude;
    });
};
/**
 * Retrieves a list of product callouts based on the provided product attributes and attribute settings.
 */
export var getProductCallouts = function (_a) {
    var 
    /**
     * The full product data
     */
    productData = _a.productData, 
    /**
     * The settings that define attribute callouts
     */
    attributeSettings = _a.attributeSettings;
    if (!attributeSettings || !(productData === null || productData === void 0 ? void 0 : productData.categories)) {
        return [];
    }
    // Find matching category settings
    var matchingSettings = attributeSettings.filter(function (resource) {
        return Array.isArray(productData.categories) &&
            productData.categories.find(function (category) { var _a; return ((_a = resource.category) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === category.toLowerCase(); });
    });
    if (!matchingSettings.length)
        return [];
    var results = [];
    matchingSettings.forEach(function (setting) {
        var callouts = setting.productAttributeCallouts || [];
        callouts.forEach(function (callout) {
            var attributes = callout.attributes || [];
            // Process attributes in callout, using some() to handle firstMatchOnly
            attributes.some(function (attribute) {
                if (shouldIncludeAttribute(attribute, productData)) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    var referenceAttributes = attribute.referenceAttributes, remainingData = __rest(attribute, ["referenceAttributes"]);
                    results.push(remainingData);
                    return callout.firstMatchOnly; // Stop if firstMatchOnly is true
                }
                return false;
            });
        });
    });
    // Dedupe callouts by label, keeping the first occurrence
    var seenLabels = new Set();
    return results.filter(function (callout) {
        var label = callout.label;
        if (!label) {
            return true;
        }
        if (seenLabels.has(label)) {
            return false;
        }
        seenLabels.add(label);
        return true;
    });
};
/**
 * Universal product enhancement function that adds callouts to any product-like object
 *
 * @returns Enhanced product item with callouts
 */
export var enhanceAnyProductWithCallouts = function (
/**
 * Product item or raw product data
 */
productItem, 
/**
 * Configuration for product cards attributes and callouts
 */
productCardsConfig, 
/**
 * Test Variation
 */
testVariation) {
    var _a;
    if (!productCardsConfig) {
        return productItem;
    }
    // Extract product data from different item structures
    var productData;
    var isNestedProduct = false;
    if (productItem.__typename === 'ProductCard' && 'product' in productItem) {
        productData = productItem.product;
        isNestedProduct = true;
    }
    else if (productItem.__typename === 'Product' ||
        'categories' in productItem ||
        ('attributes' in productItem && 'categories' in productItem)) {
        productData = productItem;
    }
    // If no valid product data found, return unchanged
    if (!productData) {
        return productItem;
    }
    // Utility function to filter out "Product Benefit" callouts
    var omitProductBenefitCallout = function (config) {
        var _a;
        return (__assign(__assign({}, config), { productAttributeCallouts: (_a = config.productAttributeCallouts) === null || _a === void 0 ? void 0 : _a.filter(function (callout) { return callout.label !== 'Product Benefit'; }) }));
    };
    // Modify productCardsConfig for test variation logic
    var modifiedProductCardsConfig = productCardsConfig;
    if (testVariation === 'test' && ((_a = productData.categories) === null || _a === void 0 ? void 0 : _a.includes('socks'))) {
        modifiedProductCardsConfig = productCardsConfig.map(function (config) {
            var _a, _b, _c;
            // Only apply sock test variation logic
            if (((_a = config.category) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'socks') {
                var hasCompressionAttribute = (_b = config.productAttributeCallouts) === null || _b === void 0 ? void 0 : _b.some(function (c) { return c.label === 'Compression Attribute'; });
                var hasProductBenefit = (_c = config.productAttributeCallouts) === null || _c === void 0 ? void 0 : _c.some(function (c) { return c.label === 'Product Benefit'; });
                // If there is NOT a "Compression Attribute" AND there is a "Product Benefit",
                // then show "Product Benefit" callouts along with other callouts
                if (!hasCompressionAttribute && hasProductBenefit) {
                    // Keep all callouts including "Product Benefit"
                    return config;
                }
                // Filter out "Product Benefit" callouts if the condition above is not met
                return omitProductBenefitCallout(config);
            }
            return config;
        });
    }
    else if (testVariation !== 'test') {
        // If testVariation is not 'test', never show "Product Benefit" callouts
        modifiedProductCardsConfig = productCardsConfig.map(omitProductBenefitCallout);
    }
    // Generate callouts for the product data
    var callouts = getProductCallouts({
        productData: productData,
        attributeSettings: modifiedProductCardsConfig
    });
    // For the test, there should only ever be 3 callouts
    if (testVariation === 'test') {
        callouts = callouts.slice(0, 3);
    }
    // Return enhanced item with callouts in the appropriate structure
    if (isNestedProduct) {
        return __assign(__assign({}, productItem), { product: __assign(__assign({}, productData), { callouts: callouts }) });
    }
    return __assign(__assign({}, productItem), { callouts: callouts });
};
/**
 * Cleans a product title by removing gendered terms (e.g., "Men's", "Women’s", "Youth", etc.),
 * compression values in the format "(00-00mmHg)", and collapsing multiple spaces into a single space.
 *
 * @param title - The original product title to be cleaned.
 * @returns The cleaned product title with specified terms and patterns removed.
 */
export var cleanProductTitle = function (title) {
    return title
        .replace(/\b(Men(?:['’]s)?|Women(?:['’]s)?|Youth|Junior|Kids|Toddler|Baby)\b/gi, '') // Remove gendered and age terms
        .replace(/\(\d{2}-\d{2}mmHg\)/gi, '') // Remove compression values
        .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
        .trim();
};
export default {};
